import { collection, query, where, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getDoc, deleteField, addDoc } from 'firebase/firestore';
import { getFirebase, getProdDb } from './firebase.js';
import { FN_BASE } from './fnBase.js';

// cUsers and profile always live in the Prod project.
// Every other collection follows the user's env via getFirebase().db.
const PROD_ONLY_COLLECTIONS = new Set(['cUsers', 'profile']);

const getDbForCollection = (collectionName) =>
  PROD_ONLY_COLLECTIONS.has(collectionName) ? getProdDb() : getFirebase().db;

// =============================================================================
// Global collections (no user ID in path) – e.g. ctemplatespublished
// =============================================================================

export const readGlobalProfiles = async (collectionName) => {
  const { auth } = getFirebase();
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot read profiles.');
    }
    let profiles;
    if (!auth.currentUser) {
      const response = await fetch(`${FN_BASE}/getVisibleProfiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetCollection: collectionName }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to fetch visible profiles via Cloud Function: ' + (result.error?.message || 'Unknown error'));
      }
      profiles = result.profiles || [];
    } else {
      // Always check admin status from Prod cUsers regardless of the target collection.
      const userDoc = await getDoc(doc(getProdDb(), 'cUsers', auth.currentUser.uid));
      let isAdminOrSuper = false;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        isAdminOrSuper = userData.isadminuser || userData.issuperuser;
      }

      let q;
      if (isAdminOrSuper) {
        q = query(collection(db, collectionName));
      } else {
        if (collectionName === 'ctemplatespublished') {
          q = query(
            collection(db, collectionName),
            where('isflag', '==', true)
          );
        } else if (collectionName === 'deliveryDetails') {
          // All authenticated staff (Admin, Super, Mover) can see all delivery plans
          q = query(collection(db, collectionName));
        } else {
          q = query(
            collection(db, collectionName),
            where('userId', '==', auth.currentUser.uid)
          );
        }
      }
      const querySnapshot = await getDocs(q);
      profiles = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    }
    return profiles;
  } catch (error) {
    console.error(`Error reading from ${collectionName}:`, error);
    throw error;
  }
};

export const writeGlobalProfile = async (collectionName, profileId, data) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot write profile.');
    }
    // null profileId → let Firestore auto-generate the document ID
    let docRef;
    if (profileId === null) {
      docRef = await addDoc(collection(db, collectionName), {
        ...data,
        created: serverTimestamp(),
      });
    } else {
      docRef = doc(db, collectionName, profileId);
      await setDoc(docRef, {
        ...data,
        created: serverTimestamp(),
      });
    }
    return docRef.id;
  } catch (error) {
    console.error(`Error writing to ${collectionName}:`, error);
    throw error;
  }
};

export const updateGlobalProfile = async (collectionName, profileId, updatedData) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot update profile.');
    }
    const docRef = doc(db, collectionName, profileId);
    await updateDoc(docRef, {
      ...updatedData,
      updated: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error updating in ${collectionName} - ${profileId}:`, error);
    throw error;
  }
};

export const deleteGlobalProfile = async (collectionName, profileId) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot delete profile.');
    }
    const docRef = doc(db, collectionName, profileId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting from ${collectionName}:`, error);
    throw error;
  }
};

// =============================================================================
// User‑scoped collections (doc ID = userId) – e.g. cUsers, profile, favourites, custom
// =============================================================================

export const readUserData = async (collectionName) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot read user data.');
    }
    const querySnapshot = await getDocs(collection(db, collectionName));
    const userData = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
    return userData;
  } catch (error) {
    console.error(`Error reading user data from ${collectionName}:`, error);
    throw error;
  }
};

export const readUserDataById = async (collectionName, userId) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot read user data.');
    }
    const docRef = doc(db, collectionName, userId);
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      return { id: docSnapshot.id, ...docSnapshot.data() };
    }
    return null;
  } catch (error) {
    console.error(`Error reading user data from ${collectionName} for user ${userId}:`, error);
    throw error;
  }
};

export const writeUserData = async (collectionName, userId, data) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot write user data.');
    }
    if (typeof userId !== 'string') {
      throw new Error(`Invalid userId: ${userId}. It must be a string.`);
    }
    const docRef = doc(db, collectionName, userId);
    await setDoc(docRef, {
      ...data,
      created: serverTimestamp(),
      updated: serverTimestamp(),
    }, { merge: true });
    return docRef.id;
  } catch (error) {
    console.error(`Error writing user data to ${collectionName}:`, error);
    throw error;
  }
};

export const updateUserData = async (collectionName, userId, updatedData) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot update user data.');
    }
    const docRef = doc(db, collectionName, userId);
    await updateDoc(docRef, {
      ...updatedData,
      updated: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error updating user data in ${collectionName}:`, error);
    throw error;
  }
};

export const deleteUserData = async (collectionName, userId) => {
  const db = getDbForCollection(collectionName);
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot delete user data.');
    }
    const docRef = doc(db, collectionName, userId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting user data from ${collectionName}:`, error);
    throw error;
  }
};

// =============================================================================
// Combinations stored in the otemplate document (oCombination, oCoverCombi, oprscontrol, oexptimeline)
// =============================================================================

export const fetchCombinations = async (tableName) => {
  const { auth, db } = getFirebase();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot read combinations.');
    }
    if (!auth.currentUser && tableName !== 'oCombination') {
      throw new Error('User is not authenticated. Cannot read otemplate data.');
    }

    const docRef = doc(db, 'otemplate', tableName);
    const docSnapshot = await getDoc(docRef);

    let resultArray = [];
    let keyPrefix;
    let keyRegex;

    // Determine the key prefix and regex based on tableName
    if (tableName === 'oCombination') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
    } else if (tableName === 'oCoverCombi') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
    } else if (tableName === 'cCakeCharge') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
    } else if (tableName === 'dDeliveryCharge') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
    } else if (tableName === 'oprscontrol') {
      keyPrefix = ['buyer', 'mover', 'super', 'admin'];
      keyRegex = /^(buyer|mover|super|admin)\d+$/;
    } else if (tableName === 'oexptimeline') {
      keyPrefix = ['change', 'option', 'stop', 'pause'];
      keyRegex = /^(change|option|stop|pause)\d+$/;
    } else {
      throw new Error(`Unsupported tableName: ${tableName}. Must be 'oCombination', 'oCoverCombi', 'cCakeCharge', 'dDeliveryCharge', 'oprscontrol', or 'oexptimeline'.`);
    }

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();

      if (data && typeof data === 'object') {
        let combiFields;
        if (tableName === 'oprscontrol') {
          combiFields = Object.keys(data).filter(key => key.match(keyRegex));
        } else if (tableName === 'oexptimeline') {
          combiFields = Object.keys(data).filter(key => key.match(keyRegex));
        } else {
          combiFields = Object.keys(data).filter(key => key.match(keyRegex));
        }

        for (const fieldName of combiFields) {
          const combo = data[fieldName];

          if (combo && Array.isArray(combo)) {
            let expectedComboLength;
            if (tableName === 'oCombination') {
              expectedComboLength = 5; // [flavour, filling, sponge, Segment, priceSegment]
            } else if (tableName === 'oCoverCombi') {
              expectedComboLength = 3; // [cover, frosting, priceSegment]
            } else if (tableName === 'cCakeCharge') {
              expectedComboLength = 3; // [variant, cost, personServed]
            } else if (tableName === 'dDeliveryCharge') {
              expectedComboLength = 2; // [distance, cost]
            } else if (tableName === 'oprscontrol') {
              expectedComboLength = 3; // [application, activity, state]
            } else if (tableName === 'oexptimeline') {
              expectedComboLength = fieldName.startsWith('stop') || fieldName.startsWith('pause') ? 2 : 3; // [requestState, showState1] or [requestState, showState1, showState2]
            }

            // Adjust the combo array to match expected length
            const adjustedCombo = new Array(expectedComboLength).fill('');
            for (let i = 0; i < Math.min(combo.length, expectedComboLength); i++) {
              adjustedCombo[i] = combo[i] || '';
            }

            if (tableName === 'oCombination') {
              const spongeOptions = adjustedCombo[2] === '-' ? ['Vanilla', 'Chocolate'] : [adjustedCombo[2]];
              resultArray.push({
                id: fieldName,
                flavour: adjustedCombo[0] || '',
                filling: adjustedCombo[1] || '',
                sponge: spongeOptions,
                segment: adjustedCombo[3] || '',
                priceSegment: adjustedCombo[4] || '',
              });
            } else if (tableName === 'oCoverCombi') {
              resultArray.push({
                id: fieldName,
                cover: adjustedCombo[0] || '',
                frosting: adjustedCombo[1] || '',
                priceSegment: adjustedCombo[2] || '',
              });
            } else if (tableName === 'cCakeCharge') {
              resultArray.push({
                id: fieldName,
                variant: adjustedCombo[0] || '',
                cost: adjustedCombo[1] || '',
                personServed: adjustedCombo[2] || '',
              });
            } else if (tableName === 'dDeliveryCharge') {
              resultArray.push({
                id: fieldName,
                distance: adjustedCombo[0] || '',
                cost: adjustedCombo[1] || '',
              });
            } else if (tableName === 'oprscontrol') {
              resultArray.push({
                id: fieldName,
                application: adjustedCombo[0] || '',
                activity: adjustedCombo[1] || '',
                state: adjustedCombo[2] || ''
              });
            } else if (tableName === 'oexptimeline') {
              resultArray.push({
                id: fieldName,
                requestState: adjustedCombo[0] || '',
                showState1: adjustedCombo[1] || '',
                showState2: expectedComboLength === 3 ? (adjustedCombo[2] || '') : ''
              });
            }
          } else {
            console.warn(`${fieldName} is not an array or is undefined:`, combo);
          }
        }
      } else {
        console.warn(`${tableName} document data is invalid:`, data);
      }
    } else {
      console.warn(`${tableName} document does not exist in otemplate collection.`);
    }
    return resultArray;
  } catch (error) {
    console.error(`Error reading data from ${tableName}:`, error.message, error.stack);
    return [];
  }
};

export const writeCombination = async (tableName, data, combiId = null) => {
  const { auth, db } = getFirebase();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot write combination.');
    }
    if (!auth.currentUser) {
      throw new Error('User is not authenticated. Cannot write to otemplate data.');
    }
    if (!['oCombination', 'oCoverCombi', 'cCakeCharge', 'dDeliveryCharge', 'oprscontrol', 'oexptimeline'].includes(tableName)) {
      throw new Error(`Invalid tableName: ${tableName}. Must be 'oCombination', 'oCoverCombi', 'cCakeCharge', 'dDeliveryCharge', 'oprscontrol', or 'oexptimeline'.`);
    }

    let keyPrefix;
    let keyRegex;
    let expectedLength;
    if (tableName === 'oCombination') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
      expectedLength = 5; // [flavour, filling, sponge, segment, priceSegment]
    } else if (tableName === 'oCoverCombi') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
      expectedLength = 3; // [cover, frosting, priceSegment]
    } else if (tableName === 'cCakeCharge') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
      expectedLength = 3; // [variant, cost, personServed]
    } else if (tableName === 'dDeliveryCharge') {
      keyPrefix = 'combi';
      keyRegex = /^combi\d+$/;
      expectedLength = 2; // [distance, cost]
    } else if (tableName === 'oprscontrol') {
      keyPrefix = ['buyer', 'mover', 'super', 'admin'];
      keyRegex = /^(buyer|mover|super|admin)\d+$/;
      expectedLength = 3; // [application, activity, state]
    } else if (tableName === 'oexptimeline') {
      keyPrefix = ['change', 'option', 'stop', 'pause'];
      keyRegex = /^(change|option|stop|pause)\d+$/;
      expectedLength = combiId && (combiId.startsWith('stop') || combiId.startsWith('pause')) ? 2 : 3; // [requestState, showState1] or [requestState, showState1, showState2]
    }

    if (!Array.isArray(data) || data.length !== expectedLength) {
      throw new Error(`Data must be an array of length ${expectedLength} for ${tableName} with combiId ${combiId}.`);
    }
    if (data.some(item => typeof item !== 'string')) {
      throw new Error('All elements in data must be strings.');
    }

    const docRef = doc(db, 'otemplate', tableName);
    const docSnapshot = await getDoc(docRef);
    let newCombiKey;

    if (tableName === 'oprscontrol' || tableName === 'oexptimeline') {
      // For oprscontrol and oexptimeline, the combiId must be provided
      if (!combiId) {
        throw new Error(`combiId must be provided for ${tableName} in the format ${tableName === 'oprscontrol' ? 'buyer<number> , mover<number> , super<number>, or admin<number>' : 'change<number>, option<number>, stop<number>, or pause<number>'}.`);
      }
      if (!combiId.match(keyRegex)) {
        throw new Error(`Invalid combiId for ${tableName}: ${combiId}. Must be in the format ${tableName === 'oprscontrol' ? 'buyer<number>, mover<number>, super<number>, or admin<number>' : 'change<number>, option<number>, stop<number>, or pause<number>'}.`);
      }

      const docData = docSnapshot.exists() ? docSnapshot.data() : {};
      if (combiId) {
        // Updating an existing record
        if (docData[combiId] && docSnapshot.exists()) {
          await updateDoc(docRef, {
            [combiId]: data,
            updated: serverTimestamp(),
          });
        } else {
          // Adding a new record
          if (docData[combiId]) {
            throw new Error(`ID ${combiId} already exists in ${tableName}.`);
          }
          if (docSnapshot.exists()) {
            await updateDoc(docRef, {
              [combiId]: data,
              updated: serverTimestamp(),
            });
          } else {
            await setDoc(docRef, {
              [combiId]: data,
              created: serverTimestamp(),
              updated: serverTimestamp(),
            });
          }
        }
        return combiId;
      }
    } else {
      // Handle oCombination and oCoverCombi as before
      if (combiId && !combiId.match(keyRegex)) {
        throw new Error(`Invalid combiId for ${tableName}: ${combiId}. Must be in the format 'combi<number>'.`);
      }
      if (docSnapshot.exists()) {
        const docData = docSnapshot.data();
        if (combiId) {
          if (!docData[combiId]) {
            throw new Error(`Invalid or non-existent combiId: ${combiId}`);
          }
          await updateDoc(docRef, {
            [combiId]: data,
            updated: serverTimestamp(),
          });
          return combiId;
        } else {
          const combiKeys = Object.keys(docData).filter(key => key.match(keyRegex));
          if (combiKeys.length === 0) {
            newCombiKey = `${keyPrefix}1`;
          } else {
            const combiNumbers = combiKeys.map(key => parseInt(key.replace(keyPrefix, '')));
            const highestNumber = Math.max(...combiNumbers);
            newCombiKey = `${keyPrefix}${highestNumber + 1}`;
          }

          await updateDoc(docRef, {
            [newCombiKey]: data,
            updated: serverTimestamp(),
          });
        }
      } else {
        newCombiKey = `${keyPrefix}1`;
        await setDoc(docRef, {
          [newCombiKey]: data,
          created: serverTimestamp(),
          updated: serverTimestamp(),
        });
      }

      return newCombiKey;
    }
  } catch (error) {
    console.error(`Error writing combination to ${tableName}:`, error.message, error.stack);
    throw error;
  }
};

// =============================================================================
// Flat‑structured records in otemplate (cEvents, cShape, dHolidayCalendar, dTimewindow, otimeline)
// =============================================================================

export const writeFlatTemplate = async (tableName, name, id = null) => {
  const { auth, db } = getFirebase();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot write flat template.');
    }
    if (!auth.currentUser) {
      throw new Error('User is not authenticated. Cannot write to otemplate data.');
    }
    if (!name || typeof name !== 'string') {
      throw new Error('Name must be a non-empty string.');
    }

    let keyPrefix;
    let keyRegex;
    let valueValidationRegex;

    // Determine key prefix, regex, and value validation based on tableName
    if (['cEvents', 'cShape', 'otimeline'].includes(tableName)) {
      keyPrefix = 'id';
      keyRegex = /^id\d+$/;
      valueValidationRegex = /.+/; // Any non-empty string
    } else if (tableName === 'dHolidayCalendar') {
      keyPrefix = 'holiday'; // Default prefix; can be overridden by id
      keyRegex = /^(holiday|offday)\d+$/;
      valueValidationRegex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format
    } else if (tableName === 'dTimewindow') {
      keyPrefix = 'weekdays'; // Default prefix; can be overridden by id
      keyRegex = /^(weekdays|weekend)\d+$/;
      valueValidationRegex = /^\d{2} Hour - \d{2} Hour$/; // e.g., "09 Hour - 12 Hour"
    } else {
      throw new Error(`Unsupported tableName: ${tableName}. Must be 'cEvents', 'cShape', 'otimeline', 'dHolidayCalendar', or 'dTimewindow'.`);
    }

    // Validate the value format
    if (!name.match(valueValidationRegex)) {
      throw new Error(`Invalid value format for ${tableName}: ${name}. Must match ${valueValidationRegex.toString()}.`);
    }

    const docRef = doc(db, 'otemplate', tableName);
    const docSnapshot = await getDoc(docRef);
    let newId;

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      if (id) {
        // Validate provided ID
        if (!id.match(keyRegex)) {
          throw new Error(`Invalid ID format for ${tableName}: ${id}. Must be in the format '${tableName === 'dHolidayCalendar' ? 'holiday<number> or offday<number>' : tableName === 'dTimewindow' ? 'weekdays<number> or weekend<number>' : 'id<number>'}'.`);
        }
        // Update existing record or create new one with provided ID
        await updateDoc(docRef, {
          [id]: name,
          updated: serverTimestamp(),
        });
        return id;
      } else {
        // Auto-generate a new ID
        const idKeys = Object.keys(data).filter(key => key.match(keyRegex));
        if (idKeys.length === 0) {
          newId = `${keyPrefix}1`;
        } else {
          // For dHolidayCalendar and dTimewindow, consider the prefix
          if (tableName === 'dHolidayCalendar' || tableName === 'dTimewindow') {
            const prefixKeys = idKeys.filter(key => key.startsWith(keyPrefix));
            if (prefixKeys.length === 0) {
              newId = `${keyPrefix}1`;
            } else {
              const numbers = prefixKeys.map(key => parseInt(key.replace(keyPrefix, '')));
              const highestNumber = Math.max(...numbers);
              newId = `${keyPrefix}${highestNumber + 1}`;
            }
          } else {
            const idNumbers = idKeys.map(key => parseInt(key.replace('id', '')));
            const highestNumber = Math.max(...idNumbers);
            newId = `id${highestNumber + 1}`;
          }

          await updateDoc(docRef, {
            [newId]: name,
            updated: serverTimestamp(),
          });
        }
      }
    } else {
      // If the document doesn't exist, create it with the new entry
      newId = id || `${keyPrefix}1`;
      if (!newId.match(keyRegex)) {
        throw new Error(`Invalid ID format for ${tableName}: ${newId}. Must be in the format '${tableName === 'dHolidayCalendar' ? 'holiday<number> or offday<number>' : tableName === 'dTimewindow' ? 'weekdays<number> or weekend<number>' : 'id<number>'}'.`);
      }
      await setDoc(docRef, {
        [newId]: name,
        created: serverTimestamp(),
        updated: serverTimestamp(),
      });
    }
    return newId;
  } catch (error) {
    console.error(`Error writing to ${tableName}:`, error.message, error.stack);
    throw error;
  }
};

export const readFlatTemplate = async (tableName) => {
  const { auth, db } = getFirebase();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot read flat template.');
    }
    if (!auth.currentUser && tableName !== 'cEvents') {
      throw new Error('User is not authenticated. Cannot read otemplate data.');
    }

    const docRef = doc(db, 'otemplate', tableName);
    const docSnapshot = await getDoc(docRef);

    let resultArray = [];
    let keyRegex;

    // Determine the key regex based on tableName
    if (['cEvents', 'cShape', 'otimeline'].includes(tableName)) {
      keyRegex = /^id\d+$/;
    } else if (tableName === 'dHolidayCalendar') {
      keyRegex = /^(holiday|offday)\d+$/;
    } else if (tableName === 'dTimewindow') {
      keyRegex = /^(weekdays|weekend)\d+$/;
    } else {
      throw new Error(`Unsupported tableName: ${tableName}. Must be 'cEvents', 'cShape', 'otimeline', 'dHolidayCalendar', or 'dTimewindow'.`);
    }

    if (docSnapshot.exists()) {
      const data = docSnapshot.data();

      if (data && typeof data === 'object') {
        Object.keys(data).forEach(key => {
          if (key.match(keyRegex)) {
            let value = data[key];
            // For dHolidayCalendar, handle Timestamp objects by converting to YYYY-MM-DD string
            if (tableName === 'dHolidayCalendar' && value && typeof value === 'object' && 'toDate' in value) {
              value = value.toDate().toISOString().split('T')[0]; // Convert Timestamp to YYYY-MM-DD
            }
            // Ensure the value is a string before adding to resultArray
            if (typeof value === 'string') {
              resultArray.push({
                id: key,
                name: value,
              });
            } else {
              console.warn(`Value for key ${key} in ${tableName} is not a string:`, value);
            }
          }
        });
      } else {
        console.warn(`${tableName} document data is invalid:`, data);
      }
    } else {
      console.warn(`${tableName} document does not exist in otemplate collection.`);
    }

    return resultArray;
  } catch (error) {
    console.error(`Error reading document from otemplate/${tableName}:`, error.message, error.stack);
    throw error;
  }
};

export const deleteTemplateRecord = async (tableName, recordId) => {
  const { auth, db } = getFirebase();
  try {
    if (!db) {
      throw new Error('Firestore is not initialized. Cannot delete template record.');
    }
    if (!auth.currentUser) {
      throw new Error('User is not authenticated. Cannot delete from otemplate data.');
    }
    if (!tableName || typeof tableName !== 'string') {
      throw new Error('tableName must be a non-empty string.');
    }
    if (!recordId || typeof recordId !== 'string') {
      throw new Error('recordId must be a non-empty string.');
    }

    const isFlatTable = ['cEvents', 'cShape', 'otimeline'].includes(tableName);
    const isComboTable = ['oCombination', 'oCoverCombi', 'cCakeCharge', 'dDeliveryCharge'].includes(tableName);
    const isOprsTable = tableName === 'oprscontrol';
    const isExpTimelineTable = tableName === 'oexptimeline';
    const isHolidayTable = tableName === 'dHolidayCalendar';
    const isTimewindowTable = tableName === 'dTimewindow';

    if (isFlatTable && !recordId.match(/^id\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'id<number>'.`);
    }
    if (isComboTable && !recordId.match(/^combi\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'combi<number>'.`);
    }
    if (isOprsTable && !recordId.match(/^(buyer|mover|super|admin)\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'buyer<number>, mover<number>, super<number>, or admin<number>'.`);
    }
    if (isExpTimelineTable && !recordId.match(/^(change|option|stop|pause)\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'change<number>, option<number>, stop<number>, or pause<number>'.`);
    }
    if (isHolidayTable && !recordId.match(/^(holiday|offday)\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'holiday<number> or offday<number>'.`);
    }
    if (isTimewindowTable && !recordId.match(/^(weekdays|weekend)\d+$/)) {
      throw new Error(`Invalid recordId for ${tableName}: ${recordId}. Must be in the format 'weekdays<number> or weekend<number>'.`);
    }
    if (!isFlatTable && !isComboTable && !isOprsTable && !isExpTimelineTable && !isHolidayTable && !isTimewindowTable) {
      throw new Error(`Invalid tableName: ${tableName}. Must be one of 'cEvents', 'cShape', 'otimeline', 'oCombination', 'oCoverCombi', 'cCakeCharge', 'dDeliveryCharge', 'oprscontrol', 'oexptimeline', 'dHolidayCalendar', or 'dTimewindow'.`);
    }

    const docRef = doc(db, 'otemplate', tableName);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      throw new Error(`Document ${tableName} does not exist in otemplate collection.`);
    }

    const data = docSnapshot.data();
    if (!data[recordId]) {
      throw new Error(`Record ${recordId} does not exist in ${tableName}.`);
    }

    await updateDoc(docRef, {
      [recordId]: deleteField(),
      updated: serverTimestamp(),
    });

  } catch (error) {
    console.error(`Error deleting record from ${tableName}:`, error.message, error.stack);
    throw error;
  }
};
